import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { downloadFileFromUrl } from '@/utils/download.util';
import { Asset, AssetFileType, UploadType } from '@core/generated/gql-types';
import { UPLOAD_FILE } from '@core/generic/uploadFile.gql';
import { DELETE_ASSET } from '@core/generic/deleteAsset.gql';
import { getFriendlyUploadNetworkErrorMessage } from '@core/util/uploadNetworkError.util';

export type AssetUploadParams = {
  tag: string;
  file: File;
  metadata?: Record<string, string>;
};

export type AssetMetadataUpdateParams = {
  tag: string;
  metadata: Record<string, string>;
};

interface UseAssetOptions {
  assets: Asset[];
  userId: string;
  eligibilityTags?: string[];
  onComplete?: () => void;
}

export const useAsset = ({
  assets,
  userId,
  eligibilityTags = [],
  onComplete,
}: UseAssetOptions) => {
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [deleteAsset] = useMutation(DELETE_ASSET);

  const getAssetByTag = (tag: string): Asset | undefined => {
    return assets.find((asset) => asset.tag === tag);
  };

  const getQualificationDocuments = (): Asset[] => {
    return assets.filter((asset) => !eligibilityTags.includes(asset.tag));
  };

  const formatUploadedDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    const date = dayjs(parseInt(dateString));
    if (!date.isValid()) return dateString;
    return date.format('D MMM YYYY');
  };

  const getUploadedBy = (asset: Asset): string => {
    if (!asset.metadata) return 'candidate';
    const metadata = asset.metadata as Record<string, unknown>;
    return (
      (metadata.uploadedBy as string) ||
      (metadata.uploaded_by as string) ||
      'candidate'
    );
  };

  const uploadAsset = async ({
    tag,
    file,
    metadata = {},
  }: AssetUploadParams): Promise<boolean> => {
    if (!userId) return false;

    const reader = new FileReader();
    return new Promise((resolve) => {
      reader.readAsDataURL(file);
      reader.onload = async () => {
        try {
          const base64String = reader.result as string;
          const fileType = file.type
            .split('/')[1]
            ?.toUpperCase() as AssetFileType;

          const { data } = await uploadFile({
            variables: {
              input: {
                fileBytes: base64String.split(',')[1],
                fileType,
                type: UploadType.Asset,
                userId,
                tag,
                metadata: {
                  ...metadata,
                  uploadedBy: 'agency',
                },
              },
            },
          });

          if (data?.uploadFile?.url) {
            toast.success('File uploaded successfully', {
              position: 'bottom-left',
              autoClose: 3000,
            });
            await onComplete?.();
            resolve(true);
            return;
          }
          resolve(false);
        } catch (error) {
          console.error('Error uploading file:', error);
          toast.error(
            getFriendlyUploadNetworkErrorMessage(
              error,
              'Failed to upload file. Please try again.',
            ),
            {
              position: 'bottom-left',
              autoClose: 3000,
            },
          );
          resolve(false);
        }
      };
      reader.onerror = () => {
        toast.error('Failed to read file. Please try again.', {
          position: 'bottom-left',
          autoClose: 3000,
        });
        resolve(false);
      };
    });
  };

  const updateAssetMetadata = async ({
    tag,
    metadata,
  }: AssetMetadataUpdateParams): Promise<boolean> => {
    if (!userId) return false;

    try {
      const { data } = await uploadFile({
        variables: {
          input: {
            fileBytes: '',
            type: UploadType.Asset,
            userId,
            tag,
            metadata,
          },
        },
      });

      if (data?.uploadFile) {
        toast.success('Document details updated', {
          position: 'bottom-left',
          autoClose: 3000,
        });
        await onComplete?.();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating asset metadata:', error);
      toast.error(
        getFriendlyUploadNetworkErrorMessage(
          error,
          'Failed to update document details. Please try again.',
        ),
        {
          position: 'bottom-left',
          autoClose: 3000,
        },
      );
      return false;
    }
  };

  const handleDownload = async (asset: Asset) => {
    if (!asset.url) return;

    const fileName =
      asset.key?.split('/')?.at(-1) || getFileName(asset) || 'document';

    await downloadFileFromUrl(asset.url, fileName);
  };

  const handleDelete = async (asset: Asset) => {
    if (!asset.id) return;
    await deleteAsset({
      variables: { id: asset.id },
      onCompleted: () => {
        toast.success('File deleted successfully', {
          position: 'bottom-left',
          autoClose: 3000,
        });
        onComplete?.();
      },
    });
  };

  const getFileName = (asset: Asset): string => {
    if (asset.url) {
      const urlParts = asset.url.split('/');
      let fileName = urlParts[urlParts.length - 1];
      if (fileName.includes('?')) {
        fileName = fileName.split('?')[0];
      }
      if (fileName && fileName.includes('.')) {
        return fileName;
      }
    }
    return `${asset.tag?.toLowerCase().replace(/_/g, '') || 'document'}.pdf`;
  };

  return {
    formatUploadedDate,
    getUploadedBy,
    uploadAsset,
    updateAssetMetadata,
    handleDownload,
    handleDelete,
    getFileName,
    getAssetByTag,
    getQualificationDocuments,
  };
};
